import { SortDirection } from "global";
import { useForm } from "../../../hooks/useForm";
import {
  BannerSortKey,
  useDeleteBannerAsAdminMutation,
  useGetBannersAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import ActiveStatus from "../../common/components/ActiveStatus";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import Table from "../../common/components/Table";
import { getTotalPages } from "../../../utilities/pagination";

interface BannerListBoxProps {
  onUpdateBanner: (bannerId: string | null) => void;
}

export default function BannerListBox({ onUpdateBanner }: BannerListBoxProps) {
  const [filters, onChange, onBatch] = useForm({
    page: 1,
    query: "",
    sortKey: BannerSortKey.CreatedAt,
    sortDirection: "ASC" as SortDirection,
  });

  const { data, loading, refetch } = useGetBannersAsAdminQuery({
    variables: {
      limit: FETCH_SIZE,
      page: filters.page,
      sortKey: BannerSortKey.CreatedAt,
      sortDirection: "ASC",
    },
  });

  const [deleteBanner] = useDeleteBannerAsAdminMutation({
    onCompleted: () => {
      window.alert("배너를 비활성화 했습니다.");
    },
    refetchQueries: ["GetBannersAsAdmin", "GetBannerCountsAsAdmin"],
  });

  const onDelete = (bannerId: string) => {
    if (window.confirm("배너를 삭제하시겠습니까?")) {
      deleteBanner({
        variables: {
          bannerId,
        },
      });
    }
  };

  const onSort = (key?: BannerSortKey, direction?: SortDirection) => {
    const updatedFilters = {
      ...filters,
      sortKey: !key ? BannerSortKey.CreatedAt : key,
      sortDirection: !direction ? "DESC" : direction,
    };

    onBatch(updatedFilters);

    refetch({
      ...updatedFilters,
    });
  };

  const banners = data?.getBannersAsAdmin.edges || [];
  const totalPages = getTotalPages(data?.getBannersAsAdmin.pageInfo.totalCount);

  return (
    <BoxWithShadow className="gap-3">
      <Table.Wrapper>
        <Table.Header
          sort={{
            key: filters.sortKey,
            direction: filters.sortDirection as SortDirection,
            onSort,
          }}
          tabs={[
            { label: "썸네일" },
            { label: "배너명", sortKey: BannerSortKey.Title },
            { label: "상태", sortKey: BannerSortKey.Status },
            { label: "노출기간", sortKey: BannerSortKey.EndAt },
            { label: "클릭 수", sortKey: BannerSortKey.ClickCount },
            { label: "등록일", sortKey: BannerSortKey.CreatedAt },
            { label: "관리" },
          ]}
        />
        <Table.Body isEmpty={!banners?.length} isLoading={loading}>
          {banners.map((banner) => {
            console.log(banner);

            return (
              <Table.Row key={banner.id}>
                <Table.Column>
                  <img
                    src={
                      banner.thumbnailMedia?.uri ||
                      "/images/default_thumbnail_image.png"
                    }
                    alt={banner.title}
                    className="h-20 w-full rounded-md object-cover"
                  />
                </Table.Column>
                <Table.Column>{banner.title}</Table.Column>
                <Table.Column>
                  <ActiveStatus status={banner.status?.name || ""} />
                </Table.Column>
                <Table.Column className="whitespace-pre text-center">
                  {`${formatDate(banner.startAt, "YY.MM.DD")}\n~\n${formatDate(banner.endAt, "YY.MM.DD")}`}
                </Table.Column>
                <Table.Column>{`${banner.clickCount}회`}</Table.Column>
                <Table.Column>
                  {formatDate(banner.createdAt, "YY.MM.DD HH:mm")}
                </Table.Column>
                <Table.RowManageButtons
                  onUpdate={() => onUpdateBanner(banner.id)}
                  onDelete={
                    banner.status?.name === "비운영"
                      ? undefined
                      : () => onDelete(banner.id)
                  }
                />
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
      <Table.Pagination
        page={filters.page}
        total={totalPages}
        onPageChange={(newPage) => onChange("page", newPage)}
      />
    </BoxWithShadow>
  );
}
